import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import {
  useSession,
  readPendingCheckout,
  clearPendingCheckout,
  PendingCheckout,
} from '../lib/auth';
import { useUserOrg } from '../lib/org';
import {
  startCheckout,
  getBillingSnapshot,
  TIER_CATALOG,
} from '../lib/billing';

/**
 * Checkout — auto-fires the Stripe checkout redirect for the signed-in
 * user's pending plan.
 *
 * Post-verify handoff (task #142):
 *   1. Visitor picks plan on marketing pricing.html → /signup?plan=…&billing=…
 *   2. Signup stashes plan into user_metadata.pending_checkout AND localStorage
 *   3. Supabase sends verification email → user clicks link → auto-signs in
 *   4. RequireSubscription sees pending_checkout → routes here to /checkout
 *   5. This page reads the plan, calls startCheckout → window.location.href = Stripe
 *   6. Stripe success_url → /welcome-app.html (task #44 dashboard step)
 *
 * Order matters: we clear the pending_checkout BEFORE navigating away,
 * otherwise browser-back onto this page would refire the redirect and trap
 * the user in an infinite loop.
 *
 * Edge cases:
 *   - Signed-in but no pending_checkout → route to /paywall (nothing to do)
 *   - Signed-in AND already has active subscription (existing customer clicked
 *     the marketing site) → route to / rather than to Stripe.
 *   - Native shells (Capacitor iOS / Android) → startCheckout throws
 *     assertWebContext; we route to /paywall which shows the native
 *     reader-mode "open on the web" panel.
 *   - Stripe/network error mid-redirect → surface a Retry button so the user
 *     isn't stuck staring at a spinner forever.
 *
 * This route is behind RequireSession (needs a signed-in user) but NOT
 * behind RequireSubscription — they're paying to acquire the subscription
 * they don't yet have.
 */
export function Checkout() {
  const { session } = useSession();
  const { org, loading: orgLoading } = useUserOrg();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingCheckout | null>(null);
  const [status, setStatus] = useState<'checking' | 'redirecting' | 'error' | 'skipped'>('checking');

  const orgId = org?.org_id ?? null;

  useEffect(() => {
    // Wait until we've resolved the org so the "already subscribed?" check
    // can fire. If there's no org yet (fresh signup, on_auth_user_created
    // trigger hasn't run) we still proceed — the pending_checkout is the
    // signal we care about.
    if (!session) return;
    if (orgLoading) return;

    let cancelled = false;

    (async () => {
      try {
        // Step 1: figure out which plan the user is buying.
        const pc = await readPendingCheckout();
        if (!cancelled) setPending(pc);

        // Step 2: if the user already has an active subscription, don't send
        // them to Stripe — they'd just double-pay. Route home. Fail-open
        // on this check (if we can't read billing, still let them checkout;
        // duplicate payment is worse than a wasted API call, but rare).
        if (orgId) {
          try {
            const b = await getBillingSnapshot(orgId);
            const alreadyActive = b.status === 'active';
            if (alreadyActive) {
              // Existing customer bounced back through the pricing page —
              // clear any stale pending_checkout and drop them at home.
              await clearPendingCheckout().catch(() => null);
              if (!cancelled) navigate('/', { replace: true });
              return;
            }
          } catch { /* fail-open: keep going to checkout */ }
        }

        // Step 3: no pending_checkout → nothing to do. Route to /paywall
        // so the user picks a plan the normal way.
        if (!pc) {
          if (!cancelled) {
            setStatus('skipped');
            navigate('/paywall', { replace: true });
          }
          return;
        }

        // Step 4: clear FIRST so browser-back doesn't retrigger, THEN
        // window.location.href = Stripe. Once we set the redirecting
        // state the button shows a loader; startCheckout awaits the
        // clear too (see clearPendingCheckout — it swallows errors on
        // purpose so a metadata write failure never blocks the redirect).
        if (!cancelled) setStatus('redirecting');
        await clearPendingCheckout();
        await startCheckout(pc.tier, pc.billing);
      } catch (e) {
        if (cancelled) return;
        // Redirect failed (env, network, native shell). Show retry so the
        // user isn't stuck. Sentry captures via the browser handler in
        // main.tsx — no explicit capture here.
        setError(e instanceof Error ? e.message : String(e));
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
    // navigate intentionally not in deps — same reason as in
    // RequireSubscription: useNavigate isn't stable across BrowserRouter
    // renders and depending on it re-fires this effect in a tight loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, orgLoading, orgId]);

  async function onRetry() {
    if (!pending) {
      navigate('/paywall', { replace: true });
      return;
    }
    setError(null);
    setStatus('redirecting');
    try {
      await clearPendingCheckout();
      await startCheckout(pending.tier, pending.billing);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
    }
  }

  const planLine = pending
    ? `${TIER_CATALOG[pending.tier].label} ${pending.billing === 'annual' ? 'Annual' : 'Monthly'}`
    : null;

  return (
    <AuthLayout title="Redirecting to checkout">
      <h1>Redirecting to secure checkout…</h1>
      <p className="muted">
        {planLine
          ? <>Taking you to Stripe to pay for <strong>{planLine}</strong>. This should only take a second.</>
          : <>Loading your plan…</>}
      </p>

      {status === 'error' && (
        <>
          <div className="banner banner-error" style={{ marginTop: 12 }}>
            {error ?? 'Something went wrong opening checkout.'}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRetry}
            style={{ marginTop: 12, width: '100%' }}
          >
            Try again
          </button>
          <p className="auth-footer" style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={() => navigate('/paywall', { replace: true })}
              style={{ background: 'none', border: 0, color: 'var(--ink-soft)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
            >
              Pick a plan instead →
            </button>
          </p>
        </>
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 24, lineHeight: 1.5 }}>
        Payments handled by Stripe. We don't see or store your card details.
        30-day free trial on every plan. Nothing is taken until day 30 — cancel before then and you pay nothing.
      </p>
    </AuthLayout>
  );
}
